/** Shared plumbing for the dev scenes (spec 06 §8): the renderer, and exact frame capture. */

import { BasicShadowMap, WebGLRenderer } from 'three';
import type { RenderPipeline } from '../render/pipeline/RenderPipeline';
import { fitRenderSize, type RenderSize, renderSize } from '../render/size';

export function num(params: URLSearchParams, key: string, fallback: number): number {
  const v = Number.parseFloat(params.get(key) ?? '');
  return Number.isFinite(v) ? v : fallback;
}

/**
 * The internal resolution for this page: `?res=1280` forces a width (captures use it, so frames
 * are the same on every machine); otherwise the screen is filled with `?px=` screen pixels per
 * texel - 1, the default, is sharp; 2 or 3 is the chunky pixel look.
 */
export function chooseSize(params: URLSearchParams): RenderSize {
  const res = num(params, 'res', 0);
  if (res > 0) return renderSize(res / 640);
  const dpr = window.devicePixelRatio || 1;
  return fitRenderSize(window.innerWidth * dpr, window.innerHeight * dpr, num(params, 'px', 1));
}

/** A renderer on `canvas` that fills the window, with hard-edged shadows. */
export function createRenderer(canvas: HTMLCanvasElement): WebGLRenderer {
  // No preserveDrawingBuffer: captures read the post target, and keeping the buffer costs a copy a frame.
  const renderer = new WebGLRenderer({ canvas, antialias: false });
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = BasicShadowMap;
  const resize = (): void => {
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    renderer.setSize(window.innerWidth, window.innerHeight, false);
  };
  resize();
  window.addEventListener('resize', resize);
  return renderer;
}

/**
 * The exact post-pass frame at the internal size as a PNG data URL (border cropped, top row
 * first), with the HUD canvas, if any, drawn over it scaled to fit.
 */
export function frameDataUrl(pipeline: RenderPipeline, overlay?: HTMLCanvasElement): string {
  const px = pipeline.readPixels();
  const { viewW, viewH, targetW, border } = pipeline.size;
  const c = document.createElement('canvas');
  c.width = viewW;
  c.height = viewH;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  const img = ctx.createImageData(viewW, viewH);
  for (let y = 0; y < viewH; y++) {
    for (let x = 0; x < viewW; x++) {
      const src = ((viewH - 1 + border - y) * targetW + (x + border)) * 4;
      const dst = (y * viewW + x) * 4;
      for (let k = 0; k < 4; k++) img.data[dst + k] = px[src + k] ?? 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  if (overlay) {
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(overlay, 0, 0, viewW, viewH);
  }
  return c.toDataURL('image/png');
}

/**
 * `window.__look`: `save(name)` draws a frame and writes it to docs/lookdev/<name>.png through
 * the dev server (vite.config.ts); `tools/shoot.mjs` drives it headlessly.
 */
export function exposeLook(
  pipeline: RenderPipeline, redraw: () => void, extra: object = {}, overlay?: HTMLCanvasElement,
): void {
  Object.assign(window, {
    __look: {
      ...extra,
      pipeline,
      redraw,
      async save(name: string): Promise<string> {
        redraw();
        const res = await fetch(`/__lookdev/save?name=${encodeURIComponent(name)}`, {
          method: 'POST',
          body: frameDataUrl(pipeline, overlay),
        });
        return res.text();
      },
    },
  });
}
